import React, { useState, useEffect } from 'react';
import CompanyManagement from '../features/companies/screens/CompanyManagement.jsx';
import PlanManagement from '../features/plans/screens/PlanManagement.jsx';
import { useNavigate, Navigate } from 'react-router-dom';
import { useContext } from 'react';
import { AuthContext } from '../context/authContext.jsx';
import { getUserFromToken, getAuthToken, clearAuth } from '../context/authContext';
import { jwtDecode } from 'jwt-decode';
import { useResponsive } from '../hooks/useResponsive';
import {
  Buildings,
  Package,
  CaretLeft,
  CaretRight,
  SignOut,
  User,
  List,
  XCircle,
  Crown,
  Car,
  Star
} from '@phosphor-icons/react';



/* ─── placeholder screens ─────────────────────────────── */
const Placeholder = ({ title }) => (
  <div className="flex flex-col items-center justify-center h-full min-h-[400px] text-content-muted">
    <Crown size={48} className="mb-4 text-content-muted opacity-60" />
    <h2 className="text-xl font-semibold text-content mb-2">{title}</h2>
    <p className="text-sm text-content-muted">Sección en construcción</p>
  </div>
);

/* ─── nav items ────────────────────────────────────────── */
const TABS = [
  { id: 'planes',   name: 'Planes',   icon: <Package size={20} /> },
  { id: 'empresas', name: 'Empresas', icon: <Buildings size={20} /> },
];

/* ═══════════════════════════════════════════════════════ */
const SuperadminDashboard = () => {
  const navigate = useNavigate();
  const [activeTab, setActiveTab]           = useState('planes');
  const [sidebarOpen, setSidebarOpen]       = useState(true);
  const [mobileSidebarOpen, setMobileSidebarOpen] = useState(false);
  const [userName, setUserName]             = useState('');
  const [userCompany, setUserCompany]       = useState('');

  const { isAuthenticated } = useContext(AuthContext);
  const { isMobile } = useResponsive();

  /* detect user */
  useEffect(() => {
    try {
      const u = getUserFromToken();
      if (u) { setUserName(u.name); setUserCompany(u.companyName); return; }
      const token = getAuthToken();
      if (token) {
        const d = jwtDecode(token);
        setUserName(d.name);
      }
    } catch { setUserName('Superadmin'); }
  }, []);

  /* mobile body lock */
  useEffect(() => {
    if (mobileSidebarOpen) document.body.classList.add('menu-open');
    else document.body.classList.remove('menu-open');
    return () => document.body.classList.remove('menu-open');
  }, [mobileSidebarOpen]);

  const handleLogout = async () => {
    try {
      const token = getAuthToken();
      await fetch('/api/audit/logout', {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
      });
    } catch { /* ignore */ }
    clearAuth();
  };

  const handleTabChange = (id) => {
    setActiveTab(id);
    if (isMobile) setMobileSidebarOpen(false);
  };

  if (!isAuthenticated) return <Navigate to="/admin/login" replace />;

  /* ─── render ──────────────────────────────────────────── */
  return (
    <div className="h-screen bg-bg dashboard-layout flex overflow-hidden">

      {/* hamburger */}
      {isMobile && !mobileSidebarOpen && (
        <button onClick={() => setMobileSidebarOpen(true)} className="mobile-menu-toggle" title="Abrir menú">
          <List size={20} />
        </button>
      )}

      {/* overlay */}
      {isMobile && (
        <div
          className={`sidebar-overlay ${mobileSidebarOpen ? 'active' : ''}`}
          onClick={() => setMobileSidebarOpen(false)}
        />
      )}

      {/* ── sidebar ─────────────────────────────────────── */}
      <div className={`fixed top-0 left-0 h-screen bg-ink-2 text-content transition-all duration-300 ease-in-out dashboard-sidebar ${sidebarOpen ? 'w-72' : 'w-20'} ${isMobile ? (mobileSidebarOpen ? 'translate-x-0 z-50 mobile-open' : '-translate-x-full z-50') : 'translate-x-0 z-30'} flex flex-col`}>

        {/* header */}
        <div className="p-4 border-b border-ink-3 flex items-center justify-between">
          {sidebarOpen ? (
            <>
              <div className="flex items-center space-x-2">
                <Car size={32} />
                <div>
                  <span className="text-lg font-semibold block leading-tight">Sequela ISP</span>
                </div>
              </div>
              <div className="flex items-center space-x-2">
                {isMobile && (
                  <button onClick={() => setMobileSidebarOpen(false)} className="text-content-muted hover:text-content transition-colors lg:hidden">
                    <XCircle size={20} />
                  </button>
                )}
                <button onClick={() => setSidebarOpen(false)} className="text-content-muted hover:text-content transition-colors hidden lg:block">
                  <CaretLeft size={20} />
                </button>
              </div>
            </>
          ) : (
            <button onClick={() => setSidebarOpen(true)} className="text-content-muted hover:text-content transition-colors">
              <CaretRight size={20} />
            </button>
          )}
        </div>
        {(
            <div className="pt-2 px-2">
              <button
                onClick={() => navigate('/admin/dashboard')}
                className={`w-full flex items-center ${sidebarOpen ? 'space-x-3 px-3' : 'justify-center px-2'} py-2 rounded-lg transition-colors text-accent-text hover:bg-[--app-sidebar-active] hover:text-accent-text`}
                title={!sidebarOpen ? 'Panel admin' : ''}
              >
                <Star size={16} />
                {sidebarOpen && <span className="text-sm font-medium">Panel admin{userCompany ? ` (${userCompany})` : ''}</span>}
              </button>
            </div>
          )}

        {/* nav */}
        <div className="flex-1 overflow-y-auto p-4">
          <nav>
            <ul className="space-y-2">
              {TABS.map((tab) => (
                <li key={tab.id}>
                  <button
                    onClick={() => handleTabChange(tab.id)}
                    className={`w-full flex items-center ${sidebarOpen ? 'space-x-3 px-3' : 'justify-center px-2'} py-2 rounded-lg transition-colors relative ${
                      activeTab === tab.id
                        ? sidebarOpen ? 'bg-[--app-sidebar-active] text-accent-text' : 'text-accent-text'
                        : 'text-content-muted hover:bg-ink-3 hover:text-content'
                    }`}
                    title={!sidebarOpen ? tab.name : ''}
                  >
                    {!sidebarOpen && activeTab === tab.id && (
                      <div className="absolute left-0 top-0 bottom-0 w-1 bg-amber rounded-r-full" />
                    )}
                    <span className="flex-shrink-0">{tab.icon}</span>
                    {sidebarOpen && <span className="text-sm font-medium">{tab.name}</span>}
                  </button>
                </li>
              ))}
            </ul>
          </nav>
        </div>

        {/* user footer */}
        <div className="p-4 border-t border-ink-3">
          <div className="flex items-center space-x-3">
            <div className="w-8 h-8 bg-surface-2 rounded-full flex items-center justify-center">
              <User size={16} />
            </div>
            {sidebarOpen && (
              <div className="flex-1">
                <p className="text-sm font-medium text-content">{userName}</p>
                <p className="text-xs text-content-muted">Superadmin</p>
              </div>
            )}
            <button onClick={handleLogout} className="text-content-muted hover:text-content transition-colors" title="Cerrar Sesión">
              <SignOut size={16} />
            </button>
          </div>
        </div>
      </div>

      {/* ── main content ────────────────────────────────── */}
      <div className="flex-1 bg-bg dashboard-main-content">
        <div className="rounded-lg text-content table-responsive page-content page-content-no-shadow">
          {activeTab === 'planes'   && <PlanManagement />}
          {activeTab === 'empresas' && <CompanyManagement onCompanyChanged={setUserCompany} />}
        </div>
      </div>

    </div>
  );
};

export default SuperadminDashboard;
